/**
 * HRResourcing.jsx
 *
 * Props
 *   isAdmin      {bool}   — true = HR admin (full CRUD); false = read-only client view
 *   clientCompany {object|null} — when role=client: { companyName, token }
 *
 * CV files are served via /api/hr/cv/:filename  (server proxy — GitHub not exposed)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Users, Grid, List, Plus, Trash2, Download, Eye,
  Search, RefreshCw, Key, AlertCircle, Check, X,
  ClipboardList, Tag, Upload, Activity, LogIn, Monitor
} from 'lucide-react';
import {
  fetchCandidates, saveCandidates,
  fetchProposals, saveProposals,
  fetchClientTokens, saveClientTokens,
  fetchActivityLog,
  appendActivityLog,

  SEED_CANDIDATES, CANDIDATE_PIPELINE_STATUSES, PROPOSAL_STATUSES,
  CANDIDATE_CATEGORIES, PROPOSAL_STAGES, STAGE_STATUSES, initStages
} from '../utils/hrGithubStorage';
import HRBulkImport from './HRBulkImport';

const GITHUB_CV_RAW_BASE =
  process.env.REACT_APP_GITHUB_CV_RAW_BASE
  || (process.env.REACT_APP_REPO_OWNER && process.env.REACT_APP_REPO_NAME
    ? `https://raw.githubusercontent.com/${process.env.REACT_APP_REPO_OWNER}/${process.env.REACT_APP_REPO_NAME}/main/hr-resourcing/resumes`
    : null);

function getGithubCvRawUrl(filename) {
  if (!filename || !GITHUB_CV_RAW_BASE) return null;
  return `${GITHUB_CV_RAW_BASE}/${encodeURIComponent(filename)}`;
}

// ── CV download helper (blob fetch — avoids React Router intercept) ────────────
async function downloadCV(filename) {
  if (!filename) return;
  try {
    const proxyUrl = `/api/hr/cv/${encodeURIComponent(filename)}`;
    let res = await fetch(proxyUrl);

    // Production fallback: if API route is unavailable, download from GitHub raw path.
    if (!res.ok) {
      const rawUrl = getGithubCvRawUrl(filename);
      if (rawUrl) {
        const rawRes = await fetch(rawUrl);
        if (rawRes.ok) {
          res = rawRes;
        }
      }
    }

    if (!res.ok) throw new Error(`Server returned ${res.status}`);

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error('[CV download] Failed:', err.message);
    alert(`Could not download "${filename}": ${err.message}`);
  }
}

// ── Small helpers ─────────────────────────────────────────────────────────────

const gradeColors = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-orange-100 text-orange-800',
  D: 'bg-red-100 text-red-800'
};

function GradeBadge({ grade, score }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${gradeColors[grade] || 'bg-gray-100 text-gray-700'}`}>
      {score} {grade}
    </span>
  );
}

function StatusBadge({ value, statuses }) {
  const s = statuses.find(x => x.value === value);
  if (!s) return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{value}</span>;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
}

// ── Stage pipeline helpers ────────────────────────────────────────────────────

function StageBadge({ value }) {
  const s = STAGE_STATUSES.find(x => x.value === value);
  if (!s) return <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">{value}</span>;
  return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>;
}

/**
 * StagePipeline — renders all 5 interview stages as labelled rows.
 * isAdmin=true → can edit all stages.
 * clientCanUpdate=true → can edit non-adminOnly stages (client_r1, client_r2, client_c1).
 */
function StagePipeline({ stages = {}, isAdmin, onStageChange, clientCanUpdate = false }) {
  const canEdit = (key) => {
    const stg = PROPOSAL_STAGES.find(s => s.key === key);
    if (isAdmin) return true;
    if (clientCanUpdate && stg && !stg.adminOnly) return true;
    return false;
  };
  return (
    <div className="mt-2 space-y-1">
      {PROPOSAL_STAGES.map(stage => {
        const val = stages[stage.key] || 'pending';
        const ss  = STAGE_STATUSES.find(s => s.value === val);
        const editable = canEdit(stage.key) && !!onStageChange;
        return (
          <div key={stage.key} className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-[88px] flex-shrink-0 text-right">{stage.label}</span>
            <span className="text-gray-200 text-xs">›</span>
            {editable ? (
              <select value={val} onChange={e => onStageChange(stage.key, e.target.value)}
                className={`text-xs border rounded px-1.5 py-0.5 min-w-[90px] font-medium ${ss?.color || 'bg-gray-100 text-gray-500'}`}>
                {STAGE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            ) : (
              <StageBadge value={val} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CategoryBadge({ value }) {
  const cat = CANDIDATE_CATEGORIES.find(c => c.value === value);
  const label = cat ? cat.label : value;
  const colorMap = {
    'java':           'bg-amber-100 text-amber-800',
    'java-fullstack': 'bg-sky-100 text-sky-800',
    'genai':          'bg-violet-100 text-violet-800',
    'dotnet':         'bg-cyan-100 text-cyan-800',
    'nodejs':         'bg-lime-100 text-lime-800',
    'devops':         'bg-teal-100 text-teal-800',
    'management':     'bg-rose-100 text-rose-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[value] || 'bg-gray-100 text-gray-600'}`}>
      {label}
    </span>
  );
}

const cvIcon = (f) => {
  if (!f) return '📎';
  const ext = f.split('.').pop().toLowerCase();
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  return '📎';
};

// ── Candidate Detail Sidebar ──────────────────────────────────────────────────

function CandidateDetail({ candidate, proposals, onClose, onUpdate, onAddProposal, onDeleteProposal, onProposalUpdate, isAdmin }) {
  const [showAddProposal, setShowAddProposal] = useState(false);
  const [newProposal, setNewProposal] = useState({ companyName: '', position: '', notes: '', comments: '' });
  const [addError,    setAddError]    = useState('');
  const [statusEdit, setStatusEdit]   = useState(candidate.status);
  const [categoryEdit, setCategoryEdit] = useState(candidate.category || 'java');

  useEffect(() => { setStatusEdit(candidate.status); setCategoryEdit(candidate.category || 'java'); }, [candidate]);

  const myProposals = proposals.filter(p => p.candidateId === candidate.id)
    .sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));

  const handleAddProposal = () => {
    if (!newProposal.companyName.trim() || !newProposal.position.trim()) return;
    // 6-month duplicate check
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const dupe = proposals.find(p =>
      p.candidateId === candidate.id &&
      p.companyName.trim().toLowerCase() === newProposal.companyName.trim().toLowerCase() &&
      new Date(p.submittedDate) >= sixMonthsAgo
    );
    if (dupe) {
      setAddError(`Already proposed to ${newProposal.companyName} on ${dupe.submittedDate}. Cannot re-propose within 6 months.`);
      return;
    }
    setAddError('');
    onAddProposal(candidate.id, newProposal);
    setNewProposal({ companyName: '', position: '', notes: '', comments: '' });
    setShowAddProposal(false);
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b bg-gray-50 sticky top-0 z-10">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base text-gray-900 truncate">{candidate.name}</h3>
          <p className="text-xs text-gray-500">{candidate.email !== 'N/A' ? candidate.email : ''}</p>
          <p className="text-xs text-gray-500">{candidate.phone !== 'N/A' ? candidate.phone : ''}</p>
        </div>
        <button onClick={onClose} className="ml-2 p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Badges */}
        <div className="flex flex-wrap gap-2 items-center">
          <GradeBadge grade={candidate.grade} score={candidate.score} />
          <CategoryBadge value={candidate.category || 'java'} />
          <span className="text-sm text-gray-600">{candidate.experience > 0 ? `${candidate.experience} yrs` : 'Exp N/A'}</span>
          {candidate.location !== 'N/A' && <span className="text-sm text-gray-500">📍 {candidate.location}</span>}
        </div>

        {/* Assessment */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Assessment</p>
          <p className="text-xs text-gray-700 leading-relaxed">{candidate.justification}</p>
        </div>

        {/* Admin controls */}
        {isAdmin && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Status</p>
              <select value={statusEdit}
                onChange={e => { setStatusEdit(e.target.value); onUpdate(candidate.id, { status: e.target.value }); }}
                className="text-sm border rounded px-2 py-1.5 w-full">
                {CANDIDATE_PIPELINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Category</p>
              <select value={categoryEdit}
                onChange={e => { setCategoryEdit(e.target.value); onUpdate(candidate.id, { category: e.target.value }); }}
                className="text-sm border rounded px-2 py-1.5 w-full">
                {CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* CV */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Resume / CV</p>
          {candidate.cvFile ? (
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={() => downloadCV(candidate.cvFile)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                <Download className="w-3.5 h-3.5" /> Download CV
              </button>
              <span className="text-xs text-gray-400">{cvIcon(candidate.cvFile)} {candidate.cvFile}</span>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No CV attached</p>
          )}
        </div>

        {/* Proposals */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase">Proposals ({myProposals.length})</p>
            {isAdmin && (
              <button onClick={() => setShowAddProposal(!showAddProposal)}
                className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-800">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            )}
          </div>

          {showAddProposal && isAdmin && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3 space-y-2">
              <input type="text" placeholder="Company name *" value={newProposal.companyName}
                onChange={e => { setNewProposal(p => ({ ...p, companyName: e.target.value })); setAddError(''); }}
                className="w-full text-sm border rounded px-2 py-1.5 bg-white" />
              <input type="text" placeholder="Position title *" value={newProposal.position}
                onChange={e => setNewProposal(p => ({ ...p, position: e.target.value }))}
                className="w-full text-sm border rounded px-2 py-1.5 bg-white" />
              <input type="text" placeholder="Notes (optional)" value={newProposal.notes}
                onChange={e => setNewProposal(p => ({ ...p, notes: e.target.value }))}
                className="w-full text-sm border rounded px-2 py-1.5 bg-white" />
              <input type="text" placeholder="Initial comment (optional)" value={newProposal.comments}
                onChange={e => setNewProposal(p => ({ ...p, comments: e.target.value }))}
                className="w-full text-sm border rounded px-2 py-1.5 bg-white" />
              {addError && <p className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{addError}</p>}
              <div className="flex gap-2">
                <Button onClick={handleAddProposal} size="sm" className="bg-blue-600 text-xs h-7">Submit</Button>
                <Button onClick={() => { setShowAddProposal(false); setAddError(''); }} variant="outline" size="sm" className="text-xs h-7">Cancel</Button>
              </div>
            </div>
          )}

          {myProposals.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No proposals yet</p>
          ) : (
            <div className="space-y-2">
              {myProposals.map(p => (
                <div key={p.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{p.companyName}</p>
                      <p className="text-xs text-gray-500">{p.position} • {p.submittedDate}</p>
                    </div>
                    {isAdmin && (
                      <button onClick={() => onDeleteProposal(p.id)} className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {/* Stage pipeline */}
                  <StagePipeline
                    stages={p.stages}
                    isAdmin={isAdmin}
                    onStageChange={isAdmin ? (stageKey, val) =>
                      onProposalUpdate(p.id, { stages: { ...(p.stages || initStages()), [stageKey]: val } })
                    : null}
                  />
                  {/* Comments (editable by admin, read-only display for others) */}
                  {isAdmin ? (
                    <input type="text" placeholder="Comment…" value={p.comments || ''}
                      onChange={e => onProposalUpdate(p.id, { comments: e.target.value })}
                      className="mt-2 w-full text-xs border rounded px-2 py-1 bg-white placeholder-gray-400" />
                  ) : (p.comments &&
                    <p className="mt-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">{p.comments}</p>
                  )}
                  {p.notes && <p className="text-xs text-gray-500 mt-1 italic">{p.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Allocation Matrix ─────────────────────────────────────────────────────────

function AllocationGrid({ candidates, proposals }) {
  const companies = [...new Set(proposals.map(p => p.companyName))].sort();
  if (companies.length === 0) return <p className="text-sm text-gray-500 text-center py-8">No proposals created yet.</p>;

  const matrix = {};
  proposals.forEach(p => {
    if (!matrix[p.candidateId]) matrix[p.candidateId] = {};
    matrix[p.candidateId][p.companyName] = p;
  });
  const proposedCandidates = candidates.filter(c => matrix[c.id]);

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left font-semibold sticky left-0 bg-gray-50 min-w-[160px]">Candidate</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-medium text-gray-600 w-16">Score</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-medium text-gray-600 min-w-[90px]">Category</th>
            {companies.map(c => (
              <th key={c} className="border border-gray-200 px-3 py-2 text-center font-medium text-gray-600 min-w-[120px]">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {proposedCandidates.map((cand, idx) => (
            <tr key={cand.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border border-gray-200 px-3 py-2 sticky left-0 bg-inherit font-medium">
                <div className="text-gray-900">{cand.name}</div>
                <div className="text-gray-400 font-normal">{cand.experience > 0 ? `${cand.experience}y` : '—'}</div>
              </td>
              <td className="border border-gray-200 px-2 py-2 text-center">
                <GradeBadge grade={cand.grade} score={cand.score} />
              </td>
              <td className="border border-gray-200 px-2 py-2 text-center">
                <CategoryBadge value={cand.category || 'java'} />
              </td>
              {companies.map(company => {
                const prop = matrix[cand.id]?.[company];
                return (
                  <td key={company} className="border border-gray-200 px-3 py-2 text-center">
                    {prop ? <StatusBadge value={prop.status} statuses={PROPOSAL_STATUSES} /> : <span className="text-gray-200">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Client Token Manager (admin only) ────────────────────────────────────────

function ClientTokenManager({ tokens, sha, onSave }) {
  const [localTokens, setLocalTokens] = useState(tokens || []);
  const [newToken, setNewToken] = useState({ companyName: '', token: '', email: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocalTokens(tokens || []); }, [tokens]);

  const generateToken = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let t = '';
    for (let i = 0; i < 16; i++) t += chars[Math.floor(Math.random() * chars.length)];
    setNewToken(p => ({ ...p, token: t }));
  };

  const addToken = () => {
    if (!newToken.companyName.trim() || !newToken.token.trim()) return;
    setLocalTokens(prev => [...prev, { ...newToken, createdAt: new Date().toISOString(), active: true }]);
    setNewToken({ companyName: '', token: '', email: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(localTokens, sha);
    setSaving(false);
  };

  const portalUrl = (token) => `${window.location.origin}${window.location.pathname}#hr-portal?token=${token}`;

  return (
    <div className="space-y-4">
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-xs text-yellow-800">
          <strong>Client Portal:</strong> Each company gets a unique access token. Share the HR Portal URL + their token. Clients log in via <em>Client Access</em> and see only their allocated candidates with CV download.
        </p>
      </div>
      <div className="p-3 bg-gray-50 rounded-lg border space-y-2">
        <p className="text-sm font-semibold text-gray-800">Add Client Company</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="text" placeholder="Company Name *" value={newToken.companyName}
            onChange={e => setNewToken(p => ({ ...p, companyName: e.target.value }))}
            className="text-sm border rounded px-2 py-1.5" />
          <input type="email" placeholder="Contact Email" value={newToken.email}
            onChange={e => setNewToken(p => ({ ...p, email: e.target.value }))}
            className="text-sm border rounded px-2 py-1.5" />
        </div>
        <div className="flex gap-2">
          <input type="text" placeholder="Access token *" value={newToken.token}
            onChange={e => setNewToken(p => ({ ...p, token: e.target.value }))}
            className="text-sm border rounded px-2 py-1.5 flex-1 font-mono" />
          <Button onClick={generateToken} variant="outline" size="sm" className="text-xs">Auto-generate</Button>
        </div>
        <Button onClick={addToken} size="sm" className="bg-blue-600 text-xs">Add Company</Button>
      </div>

      <div className="space-y-2">
        {localTokens.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No clients yet</p>
        ) : localTokens.map((t, idx) => (
          <div key={idx} className="p-3 bg-white border rounded-lg space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-sm">{t.companyName}</p>
                {t.email && <p className="text-xs text-gray-500">{t.email}</p>}
                <p className="text-xs font-mono text-blue-600 mt-0.5 select-all">{t.token}</p>
              </div>
              <div className="flex items-center gap-2 ml-2">
                <label className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={t.active !== false}
                    onChange={e => setLocalTokens(prev => prev.map((x, i) => i === idx ? { ...x, active: e.target.checked } : x))} />
                  Active
                </label>
                <button onClick={() => setLocalTokens(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Share token:</span>
              <code className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded select-all">{t.token}</code>
              <button onClick={() => navigator.clipboard.writeText(t.token)}
                className="text-xs text-gray-400 hover:text-blue-600 px-2 py-0.5 border rounded whitespace-nowrap">
                Copy
              </button>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-green-600 w-full text-sm">
        {saving ? 'Saving to GitHub…' : 'Save to GitHub'}
      </Button>
    </div>
  );
}

// ── Client View ───────────────────────────────────────────────────────────────

function ClientView({ clientCompany, candidates, proposals, onProposalUpdate }) {
  const clientProposals    = proposals.filter(p => p.companyName === clientCompany.companyName);
  const clientCandidateIds = new Set(clientProposals.map(p => p.candidateId));
  const clientCandidates   = candidates.filter(c => clientCandidateIds.has(c.id));
  const [selectedId, setSelectedId] = useState(null);
  const selected = clientCandidates.find(c => c.id === selectedId);

  // Fire-and-forget CV action logger
  const logCVAction = (type, candidate) => {
    appendActivityLog({
      type,
      company:       clientCompany.companyName,
      token:         clientCompany.token,
      candidateName: candidate.name,
      candidateId:   candidate.id,
      fileName:      candidate.cvFile,
      userAgent:     navigator.userAgent
    });
  };

  // Client stages that client can update
  const clientStageKeys = PROPOSAL_STAGES.filter(s => !s.adminOnly).map(s => s.key);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-4 rounded-xl">
        <h2 className="text-xl font-bold">Candidates for {clientCompany.companyName}</h2>
        <p className="text-purple-200 text-sm">{clientCandidates.length} candidate{clientCandidates.length !== 1 ? 's' : ''} proposed to your company</p>
      </div>

      {clientCandidates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No candidates proposed yet. Please check back later.</p>
        </div>
      ) : (
        <div className="flex gap-4">
          {/* List */}
          <div className="flex-1 min-w-0 overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <th className="border border-gray-200 px-3 py-2 text-left">Name</th>
                  <th className="border border-gray-200 px-2 py-2 text-center">Score</th>
                  <th className="border border-gray-200 px-2 py-2 text-center">Category</th>
                  <th className="border border-gray-200 px-3 py-2 text-center min-w-[260px]">Interview Rounds</th>
                  <th className="border border-gray-200 px-2 py-2 text-center">CV</th>
                </tr>
              </thead>
              <tbody>
                {clientCandidates.map((c, idx) => {
                  const myProps    = clientProposals.filter(p => p.candidateId === c.id);
                  const latestProp = myProps[0];
                  return (
                    <tr key={c.id}
                      onClick={() => setSelectedId(c.id === selectedId ? null : c.id)}
                      className={`cursor-pointer hover:bg-purple-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedId === c.id ? '!bg-purple-100' : ''}`}>
                      <td className="border border-gray-200 px-3 py-2 font-medium text-gray-900">{c.name}</td>
                      <td className="border border-gray-200 px-2 py-2 text-center"><GradeBadge grade={c.grade} score={c.score} /></td>
                      <td className="border border-gray-200 px-2 py-2 text-center"><CategoryBadge value={c.category || 'java'} /></td>
                      <td className="border border-gray-200 px-3 py-2" onClick={e => e.stopPropagation()}>
                        {latestProp ? (
                          <div className="space-y-1">
                            {clientStageKeys.map(key => {
                              const stg = PROPOSAL_STAGES.find(s => s.key === key);
                              const val = (latestProp.stages || {})[key] || 'pending';
                              const ss  = STAGE_STATUSES.find(s => s.value === val);
                              return (
                                <div key={key} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 w-16 flex-shrink-0">{stg.label}</span>
                                  <select value={val}
                                    onChange={e => onProposalUpdate(latestProp.id, {
                                      stages: { ...(latestProp.stages || initStages()), [key]: e.target.value }
                                    })}
                                    className={`text-xs border rounded px-1.5 py-0.5 font-medium ${ss?.color || 'bg-gray-100 text-gray-500'}`}>
                                    {STAGE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="border border-gray-200 px-2 py-2 text-center">
                        {c.cvFile ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={e => { e.stopPropagation(); logCVAction('cv_download', c); downloadCV(c.cvFile); }}
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-0.5 border border-green-300 rounded">
                              ↓
                            </button>
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-80 flex-shrink-0 border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-3 bg-purple-50 border-b flex items-start justify-between">
                <div>
                  <p className="font-bold text-sm text-gray-900">{selected.name}</p>
                  <p className="text-xs text-gray-500">{selected.email !== 'N/A' ? selected.email : ''}</p>
                </div>
                <button onClick={() => setSelectedId(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-3 space-y-3 overflow-y-auto" style={{ maxHeight: 560 }}>
                <div className="flex flex-wrap gap-1">
                  <GradeBadge grade={selected.grade} score={selected.score} />
                  <CategoryBadge value={selected.category || 'java'} />
                </div>
                {selected.location !== 'N/A' && <p className="text-xs text-gray-500">📍 {selected.location}</p>}
                <div className="p-2 bg-gray-50 rounded text-xs text-gray-700 leading-relaxed">{selected.justification}</div>
                {selected.cvFile && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { logCVAction('cv_download', selected); downloadCV(selected.cvFile); }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700">
                      <Download className="w-3 h-3" /> Download CV
                    </button>
                  </div>
                )}
                {/* Interview pipeline — client can update their rounds */}
                {clientProposals.filter(p => p.candidateId === selected.id).map(p => (
                  <div key={p.id} className="bg-white border rounded-lg p-2">
                    <p className="text-xs font-semibold text-gray-600 mb-1">{p.position}</p>
                    <StagePipeline
                      stages={p.stages}
                      isAdmin={false}
                      clientCanUpdate={true}
                      onStageChange={(stageKey, val) => onProposalUpdate(p.id, {
                        stages: { ...(p.stages || initStages()), [stageKey]: val }
                      })}
                    />
                    {p.comments && <p className="mt-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">{p.comments}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity Log View (admin only) ────────────────────────────────────────────

function ActivityLogView({ entries, loading, onRefresh }) {
  const [typeFilter,    setTypeFilter]    = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  const companies = [...new Set(entries.map(e => e.company).filter(Boolean))].sort();

  const filtered = [...entries]
    .filter(e => !typeFilter    || e.type    === typeFilter)
    .filter(e => !companyFilter || e.company === companyFilter)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const stats = {
    logins:    entries.filter(e => e.type === 'login').length,
    views:     entries.filter(e => e.type === 'cv_view').length,
    downloads: entries.filter(e => e.type === 'cv_download').length,
    companies: new Set(entries.map(e => e.company).filter(Boolean)).size
  };

  const typeLabel = { login: 'Login', cv_view: 'CV View', cv_download: 'CV Download' };
  const typeColor = {
    login:       'bg-blue-100 text-blue-800',
    cv_view:     'bg-purple-100 text-purple-800',
    cv_download: 'bg-green-100 text-green-800'
  };
  const TypeIcon = { login: LogIn, cv_view: Eye, cv_download: Download };

  const fmtTime = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ts; }
  };

  const exportLog = () => {
    const csv = [
      'timestamp,type,company,candidate,file,token',
      ...filtered.map(e => [
        e.timestamp, e.type, e.company || '', e.candidateName || '', e.fileName || '', e.token || ''
      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `hr-activity-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Client Logins',   value: stats.logins,    color: 'text-blue-700',   bg: 'bg-blue-50',   Icon: LogIn },
          { label: 'CV Views',        value: stats.views,     color: 'text-purple-700', bg: 'bg-purple-50', Icon: Eye },
          { label: 'CV Downloads',    value: stats.downloads, color: 'text-green-700',  bg: 'bg-green-50',  Icon: Download },
          { label: 'Active Companies',value: stats.companies, color: 'text-indigo-700', bg: 'bg-indigo-50', Icon: Monitor }
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl border p-3 flex items-center gap-3`}>
            <div className={`${s.color} opacity-70`}><s.Icon className="w-6 h-6" /></div>
            <div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="text-sm border rounded-lg px-2 py-1.5">
          <option value="">All Events</option>
          <option value="login">Logins</option>
          <option value="cv_view">CV Views</option>
          <option value="cv_download">CV Downloads</option>
        </select>
        <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
          className="text-sm border rounded-lg px-2 py-1.5">
          <option value="">All Companies</option>
          {companies.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-gray-400 flex-1">{filtered.length} event{filtered.length !== 1 ? 's' : ''}</span>
        <button onClick={onRefresh} disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        {filtered.length > 0 && (
          <button onClick={exportLog}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-800 text-white rounded-lg hover:bg-gray-700">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        )}
      </div>

      {/* Log table */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-400 mr-2" /> Loading activity log…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed">
          <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No activity recorded yet.<br />Client logins and CV interactions will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl max-h-[520px] overflow-y-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 min-w-[155px]">Date / Time</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 w-28">Event</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 min-w-[110px]">Company</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 min-w-[130px]">Candidate</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 min-w-[150px]">File</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, idx) => {
                const Icon = TypeIcon[e.type] || Activity;
                return (
                  <tr key={e.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border-b border-gray-100 px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{fmtTime(e.timestamp)}</td>
                    <td className="border-b border-gray-100 px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${typeColor[e.type] || 'bg-gray-100 text-gray-600'}`}>
                        <Icon className="w-3 h-3" /> {typeLabel[e.type] || e.type}
                      </span>
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2 font-medium text-gray-800">{e.company || '—'}</td>
                    <td className="border-b border-gray-100 px-3 py-2 text-gray-700">{e.candidateName || '—'}</td>
                    <td className="border-b border-gray-100 px-3 py-2 font-mono text-gray-500 truncate max-w-[180px]" title={e.fileName}>{e.fileName || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
// ── Main Component ────────────────────────────────────────────────────────────

const HRResourcing = ({ isAdmin = true, clientCompany = null }) => {
  const [candidates,    setCandidates]    = useState([]);
  const [proposals,     setProposals]     = useState([]);
  const [clientTokens,  setClientTokens]  = useState([]);
  const [candidatesSha, setCandidatesSha] = useState(null);
  const [proposalsSha,  setProposalsSha]  = useState(null);
  const [tokensSha,     setTokensSha]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const [activeTab,        setActiveTab]        = useState('candidates');
  const [selectedCandidate,setSelectedCandidate] = useState(null);
  const [searchQuery,      setSearchQuery]       = useState('');
  const [gradeFilter,      setGradeFilter]       = useState('');
  const [statusFilter,     setStatusFilter]      = useState('');
  const [categoryFilter,   setCategoryFilter]    = useState('');
  const [viewMode,         setViewMode]          = useState('list'); // default: list
  const [activityLog,      setActivityLog]       = useState([]);
  const [activityLogSha,   setActivityLogSha]    = useState(null);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [candResult, propResult, tokenResult, logResult] = await Promise.all([
        fetchCandidates(),
        fetchProposals(),
        fetchClientTokens(),
        isAdmin ? fetchActivityLog() : Promise.resolve({ entries: [], sha: null })
      ]);
      setCandidates(candResult.candidates.length > 0 ? candResult.candidates : SEED_CANDIDATES);
      setCandidatesSha(candResult.sha);
      setProposals(propResult.proposals);
      setProposalsSha(propResult.sha);
      setClientTokens(tokenResult.tokens);
      setTokensSha(tokenResult.sha);
      setActivityLog(logResult.entries);
      setActivityLogSha(logResult.sha);
    } catch (e) {
      setError('GitHub load error: ' + e.message + '. Using local seed data.');
      setCandidates(SEED_CANDIDATES);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error)   { const t = setTimeout(() => setError(''),   7000); return () => clearTimeout(t); } }, [error]);

  // ── Persist helpers ─────────────────────────────────────────────────────────

  const persistCandidates = useCallback(async (list, sha) => {
    setSaving(true);
    try {
      const newSha = await saveCandidates(list, sha);
      setCandidatesSha(newSha || sha);
      setSuccess('Saved to GitHub ✓');
    } catch (e) { setError('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }, []);

  const persistProposals = useCallback(async (list, sha) => {
    setSaving(true);
    try {
      const newSha = await saveProposals(list, sha);
      setProposalsSha(newSha || sha);
      setSuccess('Proposals saved ✓');
    } catch (e) { setError('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }, []);

  // ── Candidate actions ───────────────────────────────────────────────────────

  const updateCandidate = useCallback((id, updates) => {
    const updated = candidates.map(c => c.id === id ? { ...c, ...updates } : c);
    setCandidates(updated);
    persistCandidates(updated, candidatesSha);
    setSelectedCandidate(prev => prev?.id === id ? { ...prev, ...updates } : prev);
  }, [candidates, candidatesSha, persistCandidates]);

  // ── Proposal actions ────────────────────────────────────────────────────────

  const addProposal = useCallback((candidateId, data) => {
    const cand = candidates.find(c => c.id === candidateId);
    if (!cand) return;
    // 6-month duplicate guard (also validated in CandidateDetail UI)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const dupe = proposals.find(p =>
      p.candidateId === candidateId &&
      p.companyName.trim().toLowerCase() === data.companyName.trim().toLowerCase() &&
      new Date(p.submittedDate) >= sixMonthsAgo
    );
    if (dupe) return; // UI already showed the error
    // Email / phone duplicate candidate warning
    const emailDupe = cand.email && cand.email !== 'N/A' &&
      candidates.find(c => c.id !== candidateId && c.email === cand.email);
    const phoneDupe = cand.phone && cand.phone !== 'N/A' &&
      candidates.find(c => c.id !== candidateId && c.phone === cand.phone);
    if (emailDupe || phoneDupe) {
      setError(`Note: ${cand.name}'s ${emailDupe ? 'email' : 'phone'} matches another candidate (${(emailDupe || phoneDupe).name}). Verify no duplicates.`);
    }
    const newProp = {
      id: `prop_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      candidateId,
      candidateName: cand.name,
      companyName: data.companyName.trim(),
      position: data.position,
      notes: data.notes || '',
      comments: data.comments || '',
      stages: initStages(),
      status: 'submitted',
      submittedDate: new Date().toISOString().split('T')[0],
      updatedDate: new Date().toISOString()
    };
    const updated = [...proposals, newProp];
    setProposals(updated);
    persistProposals(updated, proposalsSha);
    if (cand.status === 'available') updateCandidate(candidateId, { status: 'proposed' });
  }, [candidates, proposals, proposalsSha, persistProposals, updateCandidate]);

  const updateProposalFields = useCallback((id, updates) => {
    const updated = proposals.map(p => p.id === id ? { ...p, ...updates, updatedDate: new Date().toISOString() } : p);
    setProposals(updated);
    persistProposals(updated, proposalsSha);
  }, [proposals, proposalsSha, persistProposals]);

  // Keep for backward-compat (All Proposals table old status dropdown)
  const updateProposalStatus = useCallback((id, status) => {
    updateProposalFields(id, { status });
  }, [updateProposalFields]);

  const deleteProposal = useCallback((id) => {
    if (!window.confirm('Delete this proposal?')) return;
    const updated = proposals.filter(p => p.id !== id);
    setProposals(updated);
    persistProposals(updated, proposalsSha);
  }, [proposals, proposalsSha, persistProposals]);

  const saveTokens = async (tokens, sha) => {
    setSaving(true);
    try {
      const newSha = await saveClientTokens(tokens, sha);
      setClientTokens(tokens);
      setTokensSha(newSha || sha);
      setSuccess('Client tokens saved to GitHub ✓');
    } catch (e) { setError('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Client-only view ────────────────────────────────────────────────────────
  if (!isAdmin && clientCompany) {
    return (
      <ClientView
        clientCompany={clientCompany}
        candidates={candidates}
        proposals={proposals}
        onProposalUpdate={updateProposalFields}
      />
    );
  }

  // ── Filtered candidates (admin) ─────────────────────────────────────────────

  const filtered = candidates.filter(c => {
    if (gradeFilter    && c.grade !== gradeFilter) return false;
    if (statusFilter   && c.status !== statusFilter) return false;
    if (categoryFilter && c.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.name + c.email + c.location + (c.justification || '')).toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total:         candidates.length,
    available:     candidates.filter(c => c.status === 'available').length,
    proposed:      candidates.filter(c => c.status === 'proposed').length,
    placed:        candidates.filter(c => c.status === 'placed').length,
    totalProps:    proposals.length,
    activeProps:   proposals.filter(p => !['rejected','withdrawn'].includes(p.status)).length
  };

  const TABS = [
    { id: 'candidates',  label: 'Candidates',     icon: Users },
    { id: 'allocation',  label: 'Allocation Grid', icon: Grid },
    { id: 'proposals',   label: 'All Proposals',  icon: ClipboardList },
    { id: 'access',      label: 'Client Access',  icon: Key },
    { id: 'import',      label: 'Bulk Import',    icon: Upload },
    ...(isAdmin ? [{ id: 'activity', label: 'Activity Log', icon: Activity }] : [])
  ];

  return (
    <div className="space-y-4">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 rounded-xl flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">HR Resourcing Tracker</h2>
          <p className="text-blue-100 text-sm">GitHub-backed • {candidates.length} candidates • {proposals.length} proposals</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={loadAllData} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 text-white text-xs rounded-lg hover:bg-white/30 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {saving && <span className="text-blue-200 text-xs self-center">Saving…</span>}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: 'Total',        value: stats.total,       color: 'text-gray-800' },
          { label: 'Available',    value: stats.available,   color: 'text-green-700' },
          { label: 'Proposed',     value: stats.proposed,    color: 'text-blue-700' },
          { label: 'Placed',       value: stats.placed,      color: 'text-emerald-700' },
          { label: 'Total Props',  value: stats.totalProps,  color: 'text-purple-700' },
          { label: 'Active Props', value: stats.activeProps, color: 'text-indigo-700' }
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-2.5 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Tab nav */}
        <div className="flex overflow-x-auto border-b bg-gray-50 px-2 pt-1 gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-white/60'
                }`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {/* ── CANDIDATES ── */}
          {activeTab === 'candidates' && (
            <div className="flex gap-4" style={{ minHeight: 500 }}>
              <div className="flex-1 min-w-0">
                {/* Toolbar */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <div className="relative flex-1 min-w-[160px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="text" placeholder="Search name, email, skills…" value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg" />
                  </div>
                  <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                    className="text-sm border rounded-lg px-2 py-1.5">
                    {CANDIDATE_CATEGORIES.map(c => <option key={c.value} value={c.value === 'all' ? '' : c.value}>{c.label}</option>)}
                  </select>
                  <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}
                    className="text-sm border rounded-lg px-2 py-1.5">
                    <option value="">All Grades</option>
                    {['A','B','C','D'].map(g => <option key={g} value={g}>Grade {g}</option>)}
                  </select>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="text-sm border rounded-lg px-2 py-1.5">
                    <option value="">All Status</option>
                    {CANDIDATE_PIPELINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <div className="flex border rounded-lg overflow-hidden">
                    <button onClick={() => setViewMode('list')}
                      className={`px-2 py-1.5 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                      <List className="w-4 h-4" />
                    </button>
                    <button onClick={() => setViewMode('grid')}
                      className={`px-2 py-1.5 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                      <Grid className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-3">{filtered.length} of {candidates.length} candidates</p>

                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="min-w-full text-sm border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                          <th className="border border-gray-200 px-3 py-2 text-left">Name</th>
                          <th className="border border-gray-200 px-2 py-2 w-16 text-center">Score</th>
                          <th className="border border-gray-200 px-2 py-2 w-10 text-center">Exp</th>
                          <th className="border border-gray-200 px-2 py-2 text-center min-w-[110px]">Category</th>
                          <th className="border border-gray-200 px-3 py-2 text-left min-w-[90px]">Location</th>
                          <th className="border border-gray-200 px-3 py-2 text-center">Status</th>
                          <th className="border border-gray-200 px-2 py-2 text-center">CV</th>
                          <th className="border border-gray-200 px-2 py-2 text-center">Props</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c, idx) => {
                          const activeProps = proposals.filter(p => p.candidateId === c.id && !['rejected','withdrawn'].includes(p.status));
                          const isSelected  = selectedCandidate?.id === c.id;
                          return (
                            <tr key={c.id} onClick={() => setSelectedCandidate(isSelected ? null : c)}
                              className={`cursor-pointer hover:bg-blue-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isSelected ? '!bg-blue-100' : ''}`}>
                              <td className="border border-gray-200 px-3 py-2 font-medium text-gray-900">{c.name}</td>
                              <td className="border border-gray-200 px-2 py-2 text-center"><GradeBadge grade={c.grade} score={c.score} /></td>
                              <td className="border border-gray-200 px-2 py-2 text-center text-xs">{c.experience > 0 ? `${c.experience}y` : '—'}</td>
                              <td className="border border-gray-200 px-2 py-2 text-center"><CategoryBadge value={c.category || 'java'} /></td>
                              <td className="border border-gray-200 px-3 py-2 text-xs text-gray-500">{c.location !== 'N/A' ? c.location : ''}</td>
                              <td className="border border-gray-200 px-3 py-2 text-center"><StatusBadge value={c.status} statuses={CANDIDATE_PIPELINE_STATUSES} /></td>
                              <td className="border border-gray-200 px-2 py-2 text-center">
                                {c.cvFile ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={e => { e.stopPropagation(); downloadCV(c.cvFile); }}
                                      title="Download CV" className="text-green-600 hover:text-green-800 text-xs">↓</button>
                                  </div>
                                ) : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="border border-gray-200 px-2 py-2 text-center text-xs">
                                {activeProps.length > 0
                                  ? <span className="font-semibold text-blue-600">{activeProps.length}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Grid view */
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto pr-1">
                    {filtered.map(c => {
                      const active = proposals.filter(p => p.candidateId === c.id && !['rejected','withdrawn'].includes(p.status));
                      const isSelected = selectedCandidate?.id === c.id;
                      return (
                        <div key={c.id} onClick={() => setSelectedCandidate(isSelected ? null : c)}
                          className={`p-3 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-gray-900 truncate">{c.name}</p>
                              <p className="text-xs text-gray-500 truncate">{c.location !== 'N/A' ? c.location : ''}</p>
                            </div>
                            <div className="ml-1 flex-shrink-0"><GradeBadge grade={c.grade} score={c.score} /></div>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-2">
                            <CategoryBadge value={c.category || 'java'} />
                            <StatusBadge value={c.status} statuses={CANDIDATE_PIPELINE_STATUSES} />
                          </div>
                          {active.length > 0 && (
                            <div className="mt-1 pt-1 border-t border-gray-100 text-xs text-gray-500">
                              {active.length} active proposal{active.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Detail sidebar */}
              {selectedCandidate && (
                <div className="w-80 flex-shrink-0 -m-4 ml-0 border-l border-gray-200 overflow-y-auto" style={{ maxHeight: 700 }}>
                  <CandidateDetail
                    candidate={candidates.find(c => c.id === selectedCandidate.id) || selectedCandidate}
                    proposals={proposals}
                    onClose={() => setSelectedCandidate(null)}
                    onUpdate={updateCandidate}
                    onAddProposal={addProposal}
                    onDeleteProposal={deleteProposal}
                    onProposalUpdate={updateProposalFields}
                    isAdmin={isAdmin}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── ALLOCATION GRID ── */}
          {activeTab === 'allocation' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Candidates × Companies cross-reference. Rows = candidates with ≥1 proposal.</p>
              <AllocationGrid candidates={candidates} proposals={proposals} />
            </div>
          )}

          {/* ── ALL PROPOSALS ── */}
          {activeTab === 'proposals' && (
            <div>
              <p className="text-sm text-gray-500 mb-3">{proposals.length} total proposals</p>
              {proposals.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No proposals yet. Click a candidate → Add Proposal.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <th className="border border-gray-200 px-3 py-2 text-left">Candidate</th>
                        <th className="border border-gray-200 px-2 py-2 text-center min-w-[110px]">Category</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Company</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Position</th>
                        <th className="border border-gray-200 px-3 py-2 text-center min-w-[220px]">Interview Pipeline</th>
                        <th className="border border-gray-200 px-3 py-2 text-center">Date</th>
                        <th className="border border-gray-200 px-2 py-2 min-w-[150px]">Comments</th>
                        <th className="border border-gray-200 px-2 py-2 text-center">Del</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...proposals].sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate)).map((p, idx) => {
                        const cand = candidates.find(c => c.id === p.candidateId);
                        return (
                          <tr key={p.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-200 px-3 py-2 font-medium text-sm">{p.candidateName}</td>
                            <td className="border border-gray-200 px-2 py-2 text-center">
                              <CategoryBadge value={cand?.category || 'java'} />
                            </td>
                            <td className="border border-gray-200 px-3 py-2 text-sm">{p.companyName}</td>
                            <td className="border border-gray-200 px-3 py-2 text-xs text-gray-600">{p.position}</td>
                            <td className="border border-gray-200 px-3 py-2">
                              {p.stages ? (
                                <div className="space-y-0.5">
                                  {PROPOSAL_STAGES.map(stage => {
                                    const val = p.stages[stage.key] || 'pending';
                                    const ss  = STAGE_STATUSES.find(s => s.value === val);
                                    return (
                                      <div key={stage.key} className="flex items-center gap-1.5">
                                        <span className="text-xs text-gray-400 w-20 flex-shrink-0">{stage.label}</span>
                                        <select value={val}
                                          onChange={e => updateProposalFields(p.id, {
                                            stages: { ...(p.stages || initStages()), [stage.key]: e.target.value }
                                          })}
                                          className={`text-xs border rounded px-1 py-0.5 font-medium ${ss?.color || 'bg-gray-100 text-gray-500'}`}>
                                          {STAGE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                // Legacy proposals without stages — show old status dropdown
                                <select value={p.status} onChange={e => updateProposalStatus(p.id, e.target.value)}
                                  className="text-xs border rounded px-1 py-0.5 bg-white">
                                  {PROPOSAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                              )}
                            </td>
                            <td className="border border-gray-200 px-3 py-2 text-xs text-center text-gray-500 whitespace-nowrap">{p.submittedDate}</td>
                            <td className="border border-gray-200 px-2 py-2">
                              <input type="text" value={p.comments || ''} placeholder="Comment…"
                                onChange={e => updateProposalFields(p.id, { comments: e.target.value })}
                                className="w-full text-xs border rounded px-1.5 py-0.5 bg-white placeholder-gray-300" />
                            </td>
                            <td className="border border-gray-200 px-2 py-2 text-center">
                              <button onClick={() => deleteProposal(p.id)} className="text-red-400 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── CLIENT ACCESS ── */}
          {activeTab === 'access' && (
            <div className="max-w-xl">
              <ClientTokenManager tokens={clientTokens} sha={tokensSha} onSave={saveTokens} />
            </div>
          )}

          {/* ── BULK IMPORT ── */}
          {activeTab === 'import' && (
            <HRBulkImport
              candidates={candidates}
              candidatesSha={candidatesSha}
              onImportComplete={async (newList, sha) => {
                const newSha = await saveCandidates(newList, sha);
                setCandidates(newList);
                setCandidatesSha(newSha || sha);
                setSuccess(`Import saved to GitHub ✓`);
              }}
            />
          )}

          {/* ── ACTIVITY LOG (admin only) ── */}
          {activeTab === 'activity' && isAdmin && (
            <ActivityLogView
              entries={activityLog}
              loading={loading}
              onRefresh={loadAllData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HRResourcing;
